// 사용자 관련 타입
export enum UserRole {
  ADMIN = 'ADMIN',
  GENERAL = 'GENERAL',
}

export interface User {
  user_id: string;
  username: string;
  email: string;
  role: UserRole;
  organ_name?: string;
  bubmyeng?: string;
  created_at: string;
  updated_at: string;
}

// 인증 관련 타입
export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  organ_name?: string;
  bubmyeng?: string;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

// 법령 관련 타입
export enum LawCategory {
  KYOHUN = '교헌',
  KYOGYU = '교규',
  KYORYEONG = '교령',
  YEGYU = '예규',
}

export enum LawStatus {
  ENACTED = '제정',
  AMENDED = '개정',
  FULLY_AMENDED = '전면 개정',
  ABOLISHED = '폐지',
}

export interface Law {
  law_id: string;
  title: string;
  content: string;
  category: LawCategory;
  status: LawStatus;
  resolution_date?: string;
  owner?: string;
  serial_number?: number;
  is_approved: boolean;
  approval_date?: string;
  view_count: number;
  created_at: string;
  updated_at: string;
  createdBy?: User;
  versions?: LawVersion[];
  attachments?: Attachment[];
  _isLastApprovedVersion?: boolean; // 마지막 승인된 버전임을 표시하는 플래그
}

export interface LawVersion {
  version_id: string;
  law_id: string;
  title: string;
  category: LawCategory;
  status: LawStatus;
  resolution_date?: string;
  owner?: string;
  serial_number?: number;
  content: string;
  is_approved: boolean;
  approval_date?: string;
  created_at: string;
  attachments?: Attachment[];
}

export interface CreateLawRequest {
  title: string;
  content: string;
  category: LawCategory;
  status: LawStatus;
  resolution_date?: string;
  owner?: string;
  serial_number?: number;
}

export interface UpdateLawRequest {
  title?: string;
  content?: string;
  category?: LawCategory;
  status?: LawStatus;
  resolution_date?: string;
  owner?: string;
  serial_number?: number;
  attachments?: Attachment[];
}

export interface ApproveLawRequest {
  keepHistory: boolean;
}

export interface CompareVersionsRequest {
  versionId1?: string;
  versionId2?: string;
}

export interface CompareVersionsResponse {
  version1: LawVersion | Law;
  version2: LawVersion | Law;
  diffs: DiffItem[];
  attachmentDiffs?: {
    added: Attachment[];
    removed: Attachment[];
    unchanged: Attachment[];
    hasChanges: boolean;
  };
}

export interface DiffItem {
  type: 'equal' | 'delete' | 'insert';
  text: string;
}

// 검색 관련 타입
export enum SearchType {
  SIMPLE = 'SIMPLE',
  FULL_TEXT = 'FULL_TEXT',
  ADVANCED = 'ADVANCED',
}

export enum SearchField {
  TITLE = 'title',
  CONTENT = 'content',
  BOTH = 'both',
}

export interface SearchLawsRequest {
  query?: string;
  searchType?: SearchType;
  searchField?: SearchField;
  approvedOnly?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: 'title' | 'created_at' | 'approval_date' | 'category_priority';
  sortOrder?: 'ASC' | 'DESC';
}

export interface SearchLawsResponse {
  laws: Law[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  searchQuery?: string;
  searchType: string;
  searchField: string;
  approvedOnly: boolean;
  sortBy: string;
  sortOrder: string;
}

// 공지사항 관련 타입
export interface Announcement {
  announcement_id: string;
  title: string;
  content: string;
  view_count: number;
  is_approved: boolean;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  createdBy: User;
  approvedBy?: User;
  comments?: Comment[];
  attachments?: Attachment[];
  commentCount?: number;
}

export interface CreateAnnouncementRequest {
  title: string;
  content: string;
}

export interface UpdateAnnouncementRequest {
  title?: string;
  content?: string;
}

export enum AnnouncementSearchType {
  SIMPLE = 'SIMPLE',
  FULL_TEXT = 'FULL_TEXT',
  ADVANCED = 'ADVANCED',
}

export enum AnnouncementSearchField {
  TITLE = 'title',
  CONTENT = 'content',
  BOTH = 'both',
}

export interface SearchAnnouncementsRequest {
  query?: string;
  searchType?: AnnouncementSearchType;
  searchField?: AnnouncementSearchField;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'title' | 'created_at';
  sortOrder?: 'ASC' | 'DESC';
}

export interface SearchAnnouncementsResponse {
  announcements: Announcement[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  searchQuery?: string;
  searchType: string;
  searchField: string;
  startDate?: string;
  endDate?: string;
  sortBy: string;
  sortOrder: string;
}

// 댓글 관련 타입
export interface Comment {
  comment_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  announcement?: { announcement_id: string };
  legislativeNotice?: { legislative_notice_id: string };
  user: User;
}

export interface CreateCommentRequest {
  announcementId?: string;
  legislativeNoticeId?: string;
  content: string;
}

export interface UpdateCommentRequest {
  content: string;
}

// 첨부파일 관련 타입
export interface Attachment {
  attachment_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  law?: { law_id: string };
  announcement?: { announcement_id: string };
  publication?: { publication_id: string };
}

export interface UploadAttachmentRequest {
  file: File;
  lawId?: string;
  announcementId?: string;
  publicationId?: string;
  legislativeNoticeId?: string;
}

// 오류 신고 관련 타입
export enum BugReportStatus {
  PENDING = 'PENDING',    // 대기중
  ON_HOLD = 'ON_HOLD',    // 보류
  RESOLVED = 'RESOLVED',  // 해결
}

export interface BugReport {
  report_id: string;
  subject: string;
  description: string;
  status: BugReportStatus;
  resolution_notes?: string;
  reported_at: string;
  resolved_at?: string;
  user: User;
}

export interface CreateBugReportRequest {
  subject: string;
  description: string;
}

export interface UpdateBugReportRequest {
  resolution_notes?: string;
  status?: BugReportStatus;
}

// API 응답 타입
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface UpdateProfileRequest {
  email?: string;
  organ_name?: string;
  bubmyeng?: string;
}

export interface UpdatePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// 공포·공시 관련 타입
export enum PublicationCategory {
  PUBLICATION = '공포',
  ANNOUNCEMENT = '공시',
}

export interface Publication {
  publication_id: string;
  title: string;
  content: string;
  category: PublicationCategory;
  view_count: number;
  is_approved: boolean;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  createdBy: User;
  approvedBy?: User;
  attachments?: Attachment[];
}

// 입법예고 관련 타입
export enum LegislativeNoticeCategory {
  KYOGYU = '교규',
  KYORYUNG = '교령',
  YEGYU = '예규',
}

export interface LegislativeNotice {
  legislative_notice_id: string;
  title: string;
  content: string;
  category: LegislativeNoticeCategory;
  deadline_date: string;
  view_count: number;
  comment_count?: number;
  deadline_status?: string;
  is_deadline_passed?: boolean;
  is_approved: boolean;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  createdBy: User;
  approvedBy?: User;
  attachments?: Attachment[];
  comments?: Comment[];
}

export interface CreatePublicationRequest {
  title: string;
  content: string;
  category?: PublicationCategory;
}

export interface UpdatePublicationRequest {
  title?: string;
  content?: string;
  category?: PublicationCategory;
}

export interface CreateLegislativeNoticeRequest {
  title: string;
  content: string;
  category: LegislativeNoticeCategory;
  deadline_date: string;
}

export interface UpdateLegislativeNoticeRequest {
  title?: string;
  content?: string;
  category?: LegislativeNoticeCategory;
  deadline_date?: string;
} 